import { onUnmounted, ref, watch, type ComputedRef, type Ref } from 'vue';
import type { PaginationConfig, TableMetadataState } from '../internal';

// (watch on data.length vs totalCount below stops infinite-scroll thrash when
// a parent pads `data` to `totalCount` without going through paginate — e.g.
// windowed/sparse grids.)

/** Cap a single ensureFullyLoaded request so pathological totals cannot blow up memory. */
const ENSURE_FULLY_LOADED_MAX_ONE_SHOT = 5000;
/** Safety against infinite loops if totalCount/end-detection misbehaves. */
const ENSURE_FULLY_LOADED_MAX_ROUNDS = 500;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useTablePagination<T extends Record<string, unknown>>(params: {
  /** Read-only view of the current data; used for length-based offset and end-detection fallback. */
  data: ComputedRef<T[]>;
  /** Total count across all pages. If undefined, end-detection falls back to "didn't grow". */
  totalCount: ComputedRef<number | undefined>;
  paginationLimit: ComputedRef<number>;
  /**
   * `pages` disables the scroll-driven paginate/ensureFullyLoaded paths: the
   * consumer's `data` then holds a single page and appending would corrupt it.
   */
  paginationMode?: ComputedRef<'infinite' | 'pages'>;
  isLoading: Ref<boolean>;
  /** element that contains the pagination-handle -> to check if it is still in view */
  wrapperRef: Ref<HTMLElement | undefined>;
  getTableMetadata: () => TableMetadataState<T>;
  onTableStateChange?: (config: TableMetadataState<T>) => Promise<void>;
}) {
  const paginationLimitInternal = ref(params.paginationLimit.value);
  watch(params.paginationLimit, (newLimit) => (paginationLimitInternal.value = newLimit));

  const paginationInfo = ref<PaginationConfig>({
    offset: 0,
    limit: params.paginationLimit.value,
  });
  const paginationHandleRef = ref<HTMLElement>();
  const paginationEndReached = ref<boolean>(false);

  // Parent may pad `data` to full `totalCount` (windowed/sparse). Without this,
  // the sentinel stays "not finished" and paginate() storms onTableStateChange.
  watch(
    () => [params.data.value.length, params.totalCount.value] as const,
    ([len, total]) => {
      if (total !== undefined && total > 0 && len >= total) {
        paginationEndReached.value = true;
      }
    },
    { flush: 'sync' }
  );

  const setPaginationHandleRef = (el: unknown) => {
    const instance = (el ?? undefined) as HTMLElement | undefined;
    if (paginationHandleRef.value !== instance) {
      paginationHandleRef.value = instance;
    }
  };

  function markEndIfComplete(lengthBefore: number): void {
    const total = params.totalCount.value;
    const lengthAfter = params.data.value.length;
    if (total !== undefined) {
      if (lengthAfter >= total) {
        paginationEndReached.value = true;
      }
    } else if (lengthAfter === lengthBefore) {
      // fallback heuristic: page didn't grow -> nothing more to load
      paginationEndReached.value = true;
    }
  }

  const isPagesMode = () => params.paginationMode?.value === 'pages';

  async function paginate() {
    if (
      isPagesMode() ||
      params.isLoading.value ||
      paginationEndReached.value ||
      !params.onTableStateChange
    ) {
      return;
    }

    params.isLoading.value = true;
    const lengthBefore = params.data.value.length;
    paginationInfo.value = {
      limit: paginationLimitInternal.value,
      offset: lengthBefore,
    };

    try {
      await params.onTableStateChange(params.getTableMetadata());
      markEndIfComplete(lengthBefore);
    } finally {
      params.isLoading.value = false;
    }

    // after data loaded: check if pagination handle is still visible (we didn't load enough)
    const wrapperRect = params.wrapperRef.value?.getBoundingClientRect();
    const handleRect = paginationHandleRef.value?.getBoundingClientRect();
    if (handleRect && wrapperRect && handleRect.top < wrapperRect.bottom) {
      paginate();
    }
  }

  /**
   * Drain remaining server pages until `paginationEndReached` (data covers
   * totalCount, or a page stops growing). Prefer one request for the remaining
   * tail when totalCount is known — used by "jump to end" so lazy pagination
   * does not leave the scroller short of the true last row.
   */
  async function ensureFullyLoaded(): Promise<void> {
    if (!params.onTableStateChange || isPagesMode()) {
      return;
    }

    // Wait out any in-flight scroll-pagination page so we do not race isLoading.
    let waitGuard = 0;
    while (params.isLoading.value && waitGuard++ < 200) {
      await sleep(16);
    }

    let rounds = 0;
    while (!paginationEndReached.value && rounds++ < ENSURE_FULLY_LOADED_MAX_ROUNDS) {
      if (params.isLoading.value) {
        await sleep(16);
        continue;
      }

      const total = params.totalCount.value;
      const before = params.data.value.length;
      if (total !== undefined && before >= total) {
        paginationEndReached.value = true;
        break;
      }

      // Known total → pull the rest in one shot (capped). Unknown → normal page size.
      const remaining =
        total !== undefined && total > before ? total - before : paginationLimitInternal.value;
      const limit =
        total !== undefined
          ? Math.min(Math.max(remaining, 1), ENSURE_FULLY_LOADED_MAX_ONE_SHOT)
          : paginationLimitInternal.value;

      params.isLoading.value = true;
      paginationInfo.value = {
        limit,
        offset: before,
      };
      try {
        await params.onTableStateChange(params.getTableMetadata());
        markEndIfComplete(before);
      } finally {
        params.isLoading.value = false;
      }

      // If total was wrong/stale and we still grew under the cap, loop again.
      // If we did not grow, markEndIfComplete already set end (no-total path).
      if (
        total !== undefined &&
        !paginationEndReached.value &&
        params.data.value.length === before
      ) {
        paginationEndReached.value = true;
        break;
      }
    }
  }

  let paginationObserver: IntersectionObserver | undefined;
  watch(paginationHandleRef, (value, oldValue) => {
    if (!params.onTableStateChange) {
      return;
    }
    if (!paginationObserver) {
      paginationObserver = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) {
          return;
        }
        paginate();
      });

      const wrapperRect = params.wrapperRef.value?.getBoundingClientRect();
      const handleRect = paginationHandleRef.value?.getBoundingClientRect();
      if (handleRect && wrapperRect && handleRect.top < wrapperRect.bottom) {
        paginate();
      }
    }
    if (oldValue) {
      paginationObserver.unobserve(oldValue);
    }
    if (value) {
      paginationObserver.observe(value);
    }
  });

  onUnmounted(() => {
    paginationObserver?.disconnect();
  });

  return {
    paginationInfo,
    paginationEndReached,
    paginationHandleRef,
    setPaginationHandleRef,
    paginationLimitInternal,
    paginate,
    ensureFullyLoaded,
  };
}
